import React, { useState, useMemo } from 'react';
import { Header } from './components/Header';
import { Sidebar } from './components/Sidebar';
import { InteractiveEgg } from './components/InteractiveEgg';
import { MarketCards } from './components/MarketCards';
import { BreadthChart } from './components/BreadthChart';
import { StockScanner } from './components/StockScanner';
import { StockDrawer } from './components/StockDrawer';
import { MlStudio } from './components/MlStudio';
import { SectorHeatmap } from './components/SectorHeatmap';
import { CheatSheetModal } from './components/CheatSheetModal';
import { UserGuide } from './components/UserGuide';
import { FULL_UNIVERSE_STOCKS } from './data/fullUniverseStocks';
import {
  DEFAULT_MODEL_PARAMS,
  MODEL_PROFILE_PRESETS,
  recalculateUniverse,
} from './lib/recalculate';
import {
  aggregateSectorBreadth,
  type ScannerRecord,
  type EggPhase,
  type SectorPhaseFilter,
  type ModelParameters,
} from './types';
import { Calculator } from 'lucide-react';

export const App: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'macro' | 'scanner' | 'sectors' | 'studio' | 'guide'>('macro');
  const [selectedProfile, setSelectedProfile] = useState<string>('Default Kostolany v1');
  const [modelParams, setModelParams] = useState<ModelParameters>(DEFAULT_MODEL_PARAMS);
  const [selectedPhase, setSelectedPhase] = useState<EggPhase | null>(null);
  const [selectedSector, setSelectedSector] = useState<string | null>(null);
  const [selectedStock, setSelectedStock] = useState<ScannerRecord | null>(null);
  const [isCheatSheetOpen, setIsCheatSheetOpen] = useState<boolean>(false);

  // Dynamically recalculate all 1,826 stocks, market breadth, macro gate, and walk-forward backtest metrics
  const { stocks, breadth, macro, backtest } = useMemo(
    () => recalculateUniverse(FULL_UNIVERSE_STOCKS, modelParams, '2026-10-15'),
    [modelParams]
  );

  // Pure aggregation covering ALL 1,826 stocks across all industry sectors
  const sectorBreadthItems = useMemo(() => aggregateSectorBreadth(stocks), [stocks]);

  const handleProfileChange = (profileName: string) => {
    setSelectedProfile(profileName);
    const preset = MODEL_PROFILE_PRESETS[profileName];
    if (preset) {
      setModelParams(preset);
    }
  };

  const handleApplyParams = (newParams: ModelParameters) => {
    setModelParams(newParams);
  };

  const handleRunBacktestFromSidebar = () => {
    setActiveTab('studio');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleResetToLanding = () => {
    setActiveTab('macro');
    setSelectedPhase(null);
    setSelectedSector(null);
    setSelectedStock(null);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSectorCellClick = (filter: SectorPhaseFilter) => {
    setSelectedSector(filter.sector);
    setSelectedPhase(filter.phase);
    setActiveTab('scanner');
  };

  return (
    <div className="min-h-screen bg-[#0c0e16] text-[#e2e8f0] flex flex-col font-sans antialiased selection:bg-primary-container selection:text-white">
      {/* Top Global Institutional App Bar */}
      <Header
        macro={macro}
        breadth={breadth}
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        selectedProfile={selectedProfile}
        setSelectedProfile={handleProfileChange}
        onResetToLanding={handleResetToLanding}
        onOpenCheatSheet={() => setIsCheatSheetOpen(true)}
      />

      {/* Main Workbench Shell with Sticky Left Terminal Dock + Main Workspace */}
      <div className="flex flex-1 min-h-[calc(100vh-2.75rem)] w-full bg-[#0c0e16] items-start">
        {/* Left Terminal Dock (Sticky on Scroll) */}
        <Sidebar
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          onOpenDrawer={() => setSelectedStock(stocks[0])}
          onResetToLanding={handleResetToLanding}
          onRunBacktest={handleRunBacktestFromSidebar}
          onOpenCheatSheet={() => setIsCheatSheetOpen(true)}
        />

        {/* Main Canvas */}
        <main className="flex-1 min-w-0 p-3 md:p-4 space-y-3.5 bg-[#0c0e16]">
          {activeTab === 'macro' && (
            <div className="space-y-3.5">
              {/* Main Dual-Column Grid: Left Egg Matrix (5-cols) + Right Benchmarks & Breadth (7-cols) */}
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-3.5 items-start">
                {/* Left Column (5/12): Kostolany Egg Diagram & Nodes */}
                <div className="lg:col-span-5">
                  <InteractiveEgg
                    breadth={breadth}
                    selectedPhase={selectedPhase}
                    onSelectPhase={(phase) => {
                      setSelectedPhase(phase);
                      if (phase) setActiveTab('scanner');
                    }}
                    macroPhase={macro.macro_phase}
                  />
                </div>

                {/* Right Column (7/12): 6 Market Benchmarks + 120-Day Market Breadth Stacked Area */}
                <div className="lg:col-span-7 flex flex-col gap-3.5">
                  <MarketCards />
                  <BreadthChart breadth={breadth} />
                </div>
              </div>

              {/* Compact Horizontal Diagnostic & Risk Strip (Eliminates vertical vacant space) */}
              <div className="stitch-card rounded-xl p-3.5 flex flex-col gap-3">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-[#38bdf8] shadow-[0_0_8px_rgba(56,189,248,0.6)]"></span>
                    <div className="flex flex-wrap items-baseline gap-1.5">
                      <h3 className="text-xs md:text-sm font-semibold text-slate-100">
                        深度診斷與風險部位
                      </h3>
                      <span className="text-[11px] font-mono text-sky-400/85">
                        (Risk & Diagnostics Engine)
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 text-[11px] font-mono">
                    <span className="text-sky-400/85 hidden sm:inline">
                      更新日期: {macro.trade_date} 15:30 TWSE Close
                    </span>
                    <span className="text-sky-300 bg-sky-950/60 border border-sky-500/30 px-2 py-0.5 rounded">
                      4G 1%風控
                    </span>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-stretch">
                  {/* Card 1: 人與狗理論 Dynamic Bias Engine */}
                  <div className="p-3 rounded-lg bg-[#0b0f17]/85 border border-[#182232] flex flex-col justify-between gap-2 font-mono text-xs">
                    <div className="flex items-center justify-between">
                      <div>
                        <span className="text-slate-100 font-semibold">人與狗理論</span>{' '}
                        <span className="text-[11px] text-sky-400/90">(The Man & His Dog)</span>
                      </div>
                      <span className="text-[#38bdf8] font-bold">
                        狂熱閥值 +{(modelParams.dog_euphoria_min * 100).toFixed(0)}%
                      </span>
                    </div>
                    <p className="text-[11px] text-slate-300/90 leading-relaxed font-sans">
                      主人(MA{modelParams.ma_long}基線)穩步上揚，小狗(現價波段)快步向前奔馳，當前乖離處於安全可控中段，尚未觸及急遽拉回警戒線。
                    </p>
                    <div className="w-full bg-[#131a26] h-2 rounded-full overflow-hidden flex border border-[#1e293b]/70">
                      <div className="bg-slate-600 h-full" style={{ width: '25%' }} title="低估"></div>
                      <div className="bg-primary-container h-full" style={{ width: '50%' }} title="合理上行"></div>
                      <div className="bg-amber-500 h-full" style={{ width: '25%' }} title="過熱警戒"></div>
                    </div>
                  </div>

                  {/* Card 2: 2×2 = 5-1 Valuation & Futures Basis Telemetry */}
                  <div className="p-3 rounded-lg bg-[#0b0f17]/85 border border-[#182232] flex flex-col justify-between gap-2 font-mono text-xs">
                    <div className="flex items-center justify-between">
                      <div>
                        <span className="text-slate-100 font-semibold">2×2 = 5-1 心理與期現價差</span>{' '}
                        <span className="text-[11px] text-sky-400/90">(Basis & Sentiment)</span>
                      </div>
                      <span className="text-emerald-300 font-bold">+{(macro.basis_points ?? 9.1).toFixed(1)} 點正價差</span>
                    </div>
                    <p className="text-[11px] text-slate-300/90 leading-relaxed font-sans">
                      台指期近月與加權現貨維持穩健正價差 (+{((macro.basis_pct ?? 0.0002) * 100).toFixed(2)}%)，外資與固執者籌碼穩定，全市場 {stocks.length.toLocaleString()} 檔標的覆蓋率 100%。
                    </p>
                    <div className="flex items-center justify-between text-[11px] text-sky-300/90 pt-0.5">
                      <span>買進吸籌: {(breadth.pct_buy_zone * 100).toFixed(1)}%</span>
                      <span>隨波等待: {(breadth.pct_wait_zone * 100).toFixed(1)}%</span>
                      <span className="text-emerald-300">過熱賣出: {(breadth.pct_sell_zone * 100).toFixed(1)}%</span>
                    </div>
                  </div>

                  {/* Card 3: 4G 1% 資本部位計算機快捷面板 */}
                  <div className="p-3 rounded-lg bg-[#0b0f17]/85 border border-[#182232] flex flex-col justify-between gap-2 font-mono text-xs">
                    <div className="flex items-center justify-between">
                      <div className="flex flex-col gap-0.5">
                        <span className="text-xs font-semibold text-slate-100">4G 1% 資本部位計算機</span>
                        <span className="text-[11px] text-sky-400/85">單筆最大虧損限制: 1.0% Portfolio</span>
                      </div>
                      <button
                        type="button"
                        onClick={() => setSelectedStock(stocks[0])}
                        className="px-3 py-1.5 rounded-md bg-primary-container hover:bg-blue-600 text-white text-xs font-mono font-semibold flex items-center gap-1.5 transition-all shadow-sm cursor-pointer active:scale-98 shrink-0"
                      >
                        <Calculator className="w-3.5 h-3.5 text-white" />
                        <span>試算部位</span>
                      </button>
                    </div>
                    <p className="text-[11px] text-slate-300/90 leading-relaxed font-sans">
                      依據 2×ATR{modelParams.atr_period} 動態停損與 20D 箱型前低雙重基準，自動計算台股整股 (1,000股) 與零股最佳配置上限。
                    </p>
                    <div className="flex items-center justify-between text-[10px] font-mono text-slate-400">
                      <span className="text-sky-400/80">總資金上限: {(macro.max_equity_cap * 100).toFixed(0)}%</span>
                      <span>宏觀乘數: {macro.macro_sizing_multiplier.toFixed(1)}x</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Full-Width Stock Scanner Table (No squished columns or empty side space) */}
              <div>
                <StockScanner
                  records={stocks}
                  selectedPhase={selectedPhase}
                  selectedSector={selectedSector}
                  onClearSector={() => setSelectedSector(null)}
                  onClearPhase={() => setSelectedPhase(null)}
                  onSelectStock={(stk) => setSelectedStock(stk)}
                />
              </div>

              {/* Sector Breadth Heatmap Compact Preview */}
              <div className="pt-0.5">
                <SectorHeatmap
                  items={sectorBreadthItems}
                  compact={true}
                  onExpand={() => setActiveTab('sectors')}
                  onCellClick={handleSectorCellClick}
                />
              </div>
            </div>
          )}

          {activeTab === 'sectors' && (
            <div className="space-y-3">
              <SectorHeatmap
                items={sectorBreadthItems}
                onCellClick={handleSectorCellClick}
              />
            </div>
          )}

          {activeTab === 'scanner' && (
            <div className="space-y-3">
              <StockScanner
                records={stocks}
                selectedPhase={selectedPhase}
                selectedSector={selectedSector}
                onClearSector={() => setSelectedSector(null)}
                onClearPhase={() => setSelectedPhase(null)}
                onSelectStock={(stk) => setSelectedStock(stk)}
              />
            </div>
          )}

          {activeTab === 'studio' && (
            <MlStudio
              initialParams={modelParams}
              activeProfileName={selectedProfile}
              onApplyParams={handleApplyParams}
              liveBreadth={breadth}
              liveMacro={macro}
              liveBacktest={backtest}
            />
          )}

          {activeTab === 'guide' && (
            <UserGuide
              onNavigate={(tab) => {
                setActiveTab(tab);
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}
              onOpenCalculator={() => setSelectedStock(stocks[0])}
            />
          )}
        </main>
      </div>

      {/* Slide-over Stock Drill-down Drawer */}
      <StockDrawer
        stock={selectedStock}
        onClose={() => setSelectedStock(null)}
        macroMultiplier={macro.macro_sizing_multiplier}
      />

      {/* Single-Page Kostolany Egg Cheat Sheet Modal */}
      <CheatSheetModal
        isOpen={isCheatSheetOpen}
        onClose={() => setIsCheatSheetOpen(false)}
      />
    </div>
  );
};
